import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getLocationById } from '../../../api/locations.api';
import { replacePartnerBusinessLicense } from '../../../api/partner/locations.partner-api';
import { queryKeys } from '../../../api/queryKeys';
import { SuccessToast } from '../../../adminComponents/Toasts/Toasts';
import { setTimeTo } from '../../../helpers/helpers';
import { reportError } from '../../../helpers/reportError';

/**
 * The business license of one complete location, and the upload that replaces
 * it. Read from location details, the only place the URL comes back; the date
 * range is required there but means nothing for this field.
 *
 * A replace answers with the new URL, which goes straight into the cache - the
 * spec says not to refetch for it. The old image stays on the server as
 * history but is never returned again.
 */
export const useLocationBusinessLicense = (locationId: number | null) => {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.partner.locations.businessLicense(
    locationId ?? 'none'
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const today = new Date();
      const { data } = await getLocationById(locationId as number, {
        from: setTimeTo(today, 'start'),
        to: setTimeTo(today, 'end'),
      });
      return data.data.businessLicense ?? null;
    },
    enabled: locationId != null,
  });

  const replace = useMutation({
    mutationFn: (file: Blob) => {
      const formData = new FormData();
      formData.append('file', file);
      return replacePartnerBusinessLicense(locationId as number, formData);
    },
    onSuccess: ({ data }) => {
      queryClient.setQueryData(queryKey, data.data);
      SuccessToast(data.message);
    },
    onError: (error) => reportError(error),
  });

  return {
    license: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    replace: replace.mutate,
    replacing: replace.isPending,
  };
};
